import { SharedState } from "@/common/state";
import { clone, groupBy, groupByObj, uniq } from "@/fp";
import { fixTier3XY } from "@/gql/cords";
import {
  CoordinateInputTier3,
  AxisType,
  Tier3AxisOperator,
  AxisInputTier3,
} from "@/gql/types";

const is_content_label_cord = (cord: CoordinateInputTier3) =>
  cord.x.type === AxisType.ContentLabel ||
  cord.y.type === AxisType.ContentLabel;

const extract_content_labels = (cord: CoordinateInputTier3) => {
  if (cord.x.type === AxisType.ContentLabel) {
    return cord.x.values as string[];
  } else {
    return cord.y.values as string[];
  }
};
export const merge_and_fix_cords2 = (
  _cords_before_filter: CoordinateInputTier3[],
  _other_cords: CoordinateInputTier3[]
): CoordinateInputTier3[] => {
  merge_and_fix_cords2(_cords_before_filter, _other_cords);
  const cords_before_filter = clone(_cords_before_filter, true);
  let other_cords = clone(_other_cords, true);

  const content_label_cord_index = other_cords.findIndex(is_content_label_cord);
  const original_content_label_cord = cords_before_filter.find(
    is_content_label_cord
  );

  if (content_label_cord_index > -1 && original_content_label_cord) {
    const content_label_cord = other_cords[content_label_cord_index];
    if (original_content_label_cord.x.type === AxisType.ContentLabel) {
      original_content_label_cord.x.values = extract_content_labels(
        content_label_cord
      );
      original_content_label_cord.x.operator = SharedState.those_arr_contains_different_label_types(
        original_content_label_cord.x.values
      )
        ? Tier3AxisOperator.And
        : Tier3AxisOperator.Or;
    } else {
      original_content_label_cord.y.values = extract_content_labels(
        content_label_cord
      );

      original_content_label_cord.y.operator = SharedState.those_arr_contains_different_label_types(
        original_content_label_cord.y.values
      )
        ? Tier3AxisOperator.And
        : Tier3AxisOperator.Or;
    }
    other_cords = other_cords.filter(
      (_, index) => index !== content_label_cord_index
    );
  }
  const cords_for_query = [...cords_before_filter, ...other_cords];
  return cords_for_query;
};

const has_axis = (axis: AxisType, arr: Array<[AxisType, string[]]>) => {
  return arr.find((d) => d[0] === axis);
};

const filter_out_axis = (axis: AxisType, arr: Array<[AxisType, string[]]>) => {
  const index = arr.findIndex((d) => d[0] === axis);
  if (index > -1) {
    arr.splice(index, 1);
  }
};

export const merge_and_fix_cords = (
  _cords_before_filter: CoordinateInputTier3[],
  _other_cords: CoordinateInputTier3[]
): CoordinateInputTier3[] => {
  const time_axis = _cords_before_filter[0].z;

  //  const all_axis_types = uniq(_cords_before_filter.concat(_other_cords).map(d => [d.x.type,d.y.type]).flat());
  // TODO: merge and take only values in _other if exist for the given AxisType

  // [{x,y,z}]
  // _cords_before_filter => {AxisType.ContentLabel:[...], AxisType.Insight:[...]}
  // _other_cords => {AxisType.ContentLabel:[...], AxisType.Insight:[...]}
  // const merge_take_from_first(_cords_before_filter,_other_cords)

  const flat_axis_cords_before_filter = groupByObj(
    "type",
    _cords_before_filter.reduce((acc, item) => {
      return acc.concat([item.x, item.y]);
    }, [] as AxisInputTier3[])
  );

  const flat_axis_other_cords = groupByObj(
    "type",
    _other_cords.reduce((acc, item) => {
      return acc.concat([item.x, item.y]);
    }, [] as AxisInputTier3[])
  );

  const merged = {
    ...flat_axis_cords_before_filter,
    ...flat_axis_other_cords,
  };

  const axis_and_uniq_vals: Array<[AxisType, string[]]> = Object.entries(
    merged
  ).reduce((acc, [axis, arr]) => {
    const all_axis_values = arr.reduce((acc_2, item_2) => {
      return acc_2.concat(item_2.values!);
    }, [] as string[]);
    acc.push([axis as AxisType, uniq(all_axis_values)]);
    return acc;
  }, [] as Array<[AxisType, string[]]>);

  const severity_axis = has_axis(AxisType.Severity, axis_and_uniq_vals);
  const content_label_axis = has_axis(AxisType.ContentLabel, axis_and_uniq_vals);
  const insight_axis = has_axis(AxisType.Insight, axis_and_uniq_vals);

  // When severity filtering is combined with a "content labels + insights"
  // filter, the severity constraint must be preserved as a leading
  // "severity + content labels" coordinate (sharing the same content labels and
  // time axis as the "content labels + insights" coordinate) instead of being
  // dropped. Dropping it caused high-severity reports to leak Medium/Low events
  // (bug 24707).
  if (severity_axis && content_label_axis && insight_axis) {
    const severity_values = severity_axis[1];
    const content_values = content_label_axis[1];
    const insight_values = insight_axis[1];
    const content_operator = SharedState.those_arr_contains_different_label_types(
      content_values
    )
      ? Tier3AxisOperator.And
      : Tier3AxisOperator.Or;

    const leading_and_existing_cords: CoordinateInputTier3[] = [
      fixTier3XY({
        x: { type: AxisType.Severity, values: severity_values },
        y: {
          type: AxisType.ContentLabel,
          values: content_values,
          operator: content_operator,
        },
        z: time_axis,
      }),
      fixTier3XY({
        x: {
          type: AxisType.ContentLabel,
          values: content_values,
          operator: content_operator,
        },
        y: { type: AxisType.Insight, values: insight_values },
        z: time_axis,
      }),
    ];

    // Keep any other active filters (e.g. Application, Sensitivity) as their own
    // standalone coordinates so they are not lost.
    const handled = [
      AxisType.Severity,
      AxisType.ContentLabel,
      AxisType.Insight,
      AxisType.None,
    ];
    const other_cords = axis_and_uniq_vals
      .filter(([type]) => !handled.includes(type))
      .map(([type, values]) =>
        fixTier3XY({
          x: { type, values },
          y: { type: AxisType.None, values: [] },
          z: time_axis,
        })
      );

    return [...leading_and_existing_cords, ...other_cords];
  }

  // Severity cannot be paired with Insight directly (not a valid coordinate),
  // so when there is no ContentLabel to anchor it, fall back to dropping it.
  if (severity_axis && insight_axis) {
    filter_out_axis(AxisType.Severity, axis_and_uniq_vals);
  }
  filter_out_axis(AxisType.None, axis_and_uniq_vals);
  {
    axis_and_uniq_vals;
  }
  let final_cords: CoordinateInputTier3[] = [];
  switch (axis_and_uniq_vals.length) {
    case 0:
      final_cords = [
        fixTier3XY({
          x: {
            type: AxisType.None,
            values: ["none"],
          },
          y: {
            type: AxisType.None,
            values: [],
          },
          z: time_axis,
        }),
      ];
      break;
    case 1:
      final_cords = [
        fixTier3XY({
          x: {
            type: axis_and_uniq_vals[0][0],
            values: axis_and_uniq_vals[0][1],
          },
          y: {
            type: AxisType.None,
            values: [],
          },
          z: time_axis,
        }),
      ];
      break;
    case 2:
      final_cords = [
        fixTier3XY({
          x: {
            type: axis_and_uniq_vals[0][0],
            values: axis_and_uniq_vals[0][1],
          },
          y: {
            type: axis_and_uniq_vals[1][0],
            values: axis_and_uniq_vals[1][1],
          },
          z: time_axis,
        }),
      ];
      break;
    default:
      final_cords = [
        fixTier3XY({
          x: {
            type: axis_and_uniq_vals[0][0],
            values: axis_and_uniq_vals[0][1],
          },
          y: {
            type: axis_and_uniq_vals[1][0],
            values: axis_and_uniq_vals[1][1],
          },
          z: time_axis,
        }),
        ...axis_and_uniq_vals.slice(2).map(([type, values]) => {
          return fixTier3XY({
            x: {
              type,
              values,
            },
            y: {
              type: AxisType.None,
              values: [],
            },
            z: time_axis,
          });
        }),
      ];
      break;
  }
  return final_cords;
};
